<script>
import Layout from '@/views/layouts/main'
import PageHeader from '@/components/page-header'
import fiscalContext from '@/modules/nfse/application/nfsePageContext'
import { createManualDraft, createManualItem, FISCAL_STATES, fiscalStatusLabel, fiscalStatusVariant, formatFiscalMoney, needsReconciliation } from '@/modules/nfse/application/nfseWorkflowContract'

const documentFilters = () => ({ status: '', page: 1, per_page: 25 })
export default {
  name: 'NfseEmissionPage',
  page: { title: 'Emissão de NFS-e' },
  components: { Layout, PageHeader },
  mixins: [fiscalContext],
  data() {
    return {
      mode: 'manual', draft: createManualDraft(), confirmed: false,
      operationId: '', documentFilters: documentFilters(),
      cancelTarget: null, cancelReason: '', cancelConfirmed: false,
      fiscalStates: FISCAL_STATES, clockNow: Date.now(), clockTimer: null,
    }
  },
  computed: {
    state() { return this.flow.state },
    canEmit() { return this.mode !== 'external' && this.clockNow > 0 && this.canFiscal('nfse.emit') && this.confirmed && this.flow.canEmit() },
    documentPages() { return Math.max(1, Math.ceil(this.state.documentTotal / this.documentFilters.per_page)) },
    previewExpired() { return this.state.preview && Date.parse(this.state.preview.expires_at) <= this.clockNow },
    summary() { return this.state.preview || this.state.operation },
    partialResult() {
      return this.summary?.groups?.some(group => group.status === 'authorized') && this.summary.groups.some(group => group.status !== 'authorized')
    },
    fiscalSnapshot() {
      const snapshot = this.summary?.snapshot || {}
      return { competencia: snapshot.competencia || this.summary?.competencia, tomador: snapshot.tomador?.nome || this.summary?.tomador?.nome }
    },
    canStartNew() {
      const terminal = status => ['authorized', 'rejected', 'cancelled'].includes(status)
      return this.canFiscal('nfse.preview') && !this.state.busy && terminal(this.state.operation?.status)
        && !needsReconciliation(this.state.operation) && this.state.operation.groups?.length > 0
        && this.state.operation.groups.every(group => terminal(group.status) && !needsReconciliation(group))
    },
  },
  watch: {
    draft: { deep: true, handler() { this.confirmed = false; if (this.flow) this.flow.invalidatePreview() } },
    mode() { this.confirmed = false; this.flow.invalidatePreview() },
    '$route.query.operation_id'(id) { if (id) this.recover(id) },
  },
  mounted() {
    this.clockTimer = setInterval(() => { this.clockNow = Date.now() }, 1000)
    if (this.$route.query.operation_id) {
      this.mode = 'contextual'
      this.recover(this.$route.query.operation_id)
    }
    this.loadDocuments()
  },
  beforeDestroy() { clearInterval(this.clockTimer) },
  methods: {
    money: formatFiscalMoney,
    statusLabel: fiscalStatusLabel,
    statusVariant: fiscalStatusVariant,
    needsReconciliation,
    resetFiscalPage() {
      this.mode = 'manual'
      this.draft = createManualDraft()
      this.confirmed = false
      this.operationId = ''
      this.cancelTarget = null
      this.cancelReason = ''
      this.cancelConfirmed = false
      this.documentFilters = documentFilters()
      if (this.$route.query.operation_id) this.$router.replace({ name: 'nfse', query: {} })
    },
    newEmission() {
      if (!this.canStartNew) return
      this.flow.changeScope(this.flow.scope)
      this.resetFiscalPage()
      this.loadDocuments()
    },
    addManualItem() {
      this.draft.itens.push(createManualItem(`item-${Date.now()}-${this.draft.itens.length + 1}`))
    },
    async preview() {
      if (!this.canFiscal('nfse.preview')) return
      this.confirmed = false
      if (this.mode === 'manual') await this.flow.previewManual(this.draft)
      if (this.mode === 'contextual') await this.flow.previewContext()
      if (this.state.preview) this.rememberOperation()
    },
    rememberOperation() {
      if (this.state.operation && this.$route.query.operation_id !== this.state.operation.operation_id) {
        this.$router.replace({ name: 'nfse', query: { operation_id: this.state.operation.operation_id } })
      }
    },
    async emit() {
      if (this.mode === 'external' || !this.canFiscal('nfse.emit')) return
      await this.flow.emit(this.confirmed)
      this.confirmed = false
    },
    async recover(id = this.operationId) {
      const canDocuments = this.canFiscal('nfse.documents.view')
      const canPreview = this.canFiscal('nfse.preview')
      if (!canDocuments && !canPreview) {
        this.state.message = 'A consulta do contexto exige permissão de preview ou de consulta de documentos/operações.'
        return
      }
      // Atualizar apenas a URL de um preview recém-criado não deve invalidá-lo.
      if (this.state.preview && this.state.preview.operation_id === id) return
      this.confirmed = false
      await this.flow.recover(String(id || '').trim(), canDocuments ? {} : { preview: true })
      if (this.state.operation) this.operationId = this.state.operation.operation_id
    },
    async loadDocuments(page = 1) {
      if (!this.canFiscal('nfse.documents.view')) return
      this.documentFilters.page = page
      const filters = { page, per_page: this.documentFilters.per_page }
      if (this.documentFilters.status) filters.status = this.documentFilters.status
      await this.flow.loadDocuments(filters)
    },
    startCancellation(document) {
      if (!this.canFiscal('nfse.cancel') || document.status !== 'authorized') return
      this.cancelTarget = document
      this.cancelReason = ''
      this.cancelConfirmed = false
    },
    async cancel() {
      if (!this.canFiscal('nfse.cancel') || !this.cancelTarget) return
      await this.flow.cancelDocument(this.cancelTarget, this.cancelReason, this.cancelConfirmed)
      this.cancelTarget = null
    },
  },
}
</script>

<template>
  <Layout>
    <PageHeader title="Emissão de NFS-e" :items="[{ text: 'Financeiro' }, { text: 'NFS-e', active: true }]" />
    <div class="card"><div class="card-body">
      <div class="d-flex flex-wrap justify-content-between align-items-start mb-3">
        <div><h1 class="h4">Emissão e acompanhamento fiscal</h1><p class="text-muted">A emissão de NFS-e não cria ContaReceber. Os registros financeiros existentes são preservados.</p></div>
        <router-link v-if="canFiscal('faturamento.evolucoes.view')" to="/financeiro/faturamento-evolucoes" class="btn btn-outline-primary">Selecionar evoluções</router-link>
      </div>
      <p v-if="state.message" class="alert alert-warning" role="alert" data-test="message">{{ state.message }}</p>
      <button v-if="canStartNew" data-test="new-emission" class="btn btn-outline-primary mb-3" @click="newEmission">Nova emissão manual</button>
      <p v-if="!canFiscal('nfse.preview') && !canFiscal('nfse.documents.view')" role="alert">Sem permissão para preparar ou consultar NFS-e.</p>
      <label v-if="canFiscal('nfse.preview')" class="d-block">Modalidade
        <select v-model="mode" class="form-control mb-3" :disabled="state.busy || state.sent">
          <option value="manual">Emitir manualmente, sem evoluções</option>
          <option value="contextual">Emitir a partir de evoluções</option>
          <option value="external">Registrar NFS-e emitida externamente</option>
        </select>
      </label>
      <div v-if="mode === 'external'" class="alert alert-warning">
        Registro externo: endpoint não confirmado. O registro exige número/referência, emitente, ambiente, origem e documento comprobatório. Nenhuma transmissão será simulada.
      </div>
      <form v-if="mode === 'manual' && canFiscal('nfse.preview')" data-test="manual-form" @submit.prevent="preview">
        <fieldset :disabled="state.busy || state.sent">
          <legend class="h5">Tomador fiscal</legend>
          <p class="text-muted">Informe quem receberá a nota. O tomador pode ser diferente do paciente beneficiário.</p>
          <div class="row">
            <label class="col-md-6 mb-3">Nome / razão social *<input v-model="draft.tomador.nome" class="form-control" required /></label>
            <label class="col-md-2 mb-3">Tipo<select v-model="draft.tomador.documento.tipo" class="form-control"><option value="cpf">CPF</option><option value="cnpj">CNPJ</option></select></label>
            <label class="col-md-3 mb-3">CPF / CNPJ *<input v-model="draft.tomador.documento.numero" class="form-control" required /></label>
            <label class="col-md-6 mb-3">E-mail<input v-model="draft.tomador.email" class="form-control" type="email" /></label>
            <label class="col-md-3 mb-3">Competência *<input v-model="draft.competencia" class="form-control" type="month" required /></label>
            <label class="col-md-5 mb-3">Logradouro *<input v-model="draft.tomador.endereco.logradouro" class="form-control" required /></label>
            <label class="col-md-2 mb-3">Número *<input v-model="draft.tomador.endereco.numero" class="form-control" required /></label>
            <label class="col-md-5 mb-3">Bairro *<input v-model="draft.tomador.endereco.bairro" class="form-control" required /></label>
            <label class="col-md-4 mb-3">CEP *<input v-model="draft.tomador.endereco.cep" class="form-control" required /></label>
            <label class="col-md-4 mb-3">Município (código IBGE) *<input v-model="draft.tomador.endereco.municipio_codigo_ibge" class="form-control" maxlength="7" required /></label>
            <label class="col-md-4 mb-3">UF *<input v-model="draft.tomador.endereco.uf" class="form-control" maxlength="2" required /></label>
          </div>
          <h2 class="h5">Serviços</h2>
          <div v-for="(item, index) in draft.itens" :key="index" class="row border-bottom py-2">
            <label class="col-md-4">Descrição *<input v-model="item.descricao" class="form-control" required /></label>
            <label class="col-md-2">Código municipal *<input v-model="item.codigo_servico_municipal" class="form-control" required /></label>
            <label class="col-md-2">Item da lista *<input v-model="item.item_lista_servico" class="form-control" required /></label>
            <label class="col-md-2">Quantidade *<input v-model="item.quantidade" class="form-control" inputmode="decimal" required /></label>
            <label class="col-md-2">Valor unitário *<input v-model="item.valor_unitario" class="form-control" inputmode="decimal" required /></label>
            <label class="col-md-2">Desconto *<input v-model="item.desconto" data-test="manual-discount" class="form-control" inputmode="decimal" required /></label>
            <div class="col-md-2 d-flex align-items-center"><button type="button" class="btn btn-outline-danger" :disabled="draft.itens.length === 1" :aria-label="'Remover serviço ' + (index + 1)" @click="draft.itens.splice(index, 1)">Remover</button></div>
          </div>
          <button type="button" class="btn btn-outline-secondary my-3 mr-2" @click="addManualItem">Adicionar serviço</button>
          <button type="submit" class="btn btn-primary my-3">Gerar preview</button>
          <p class="text-muted">Tributos e valores finais vêm da configuração fiscal da empresa, validada pelo servidor. Configuração ausente impede o preview e a emissão.</p>
        </fieldset>
      </form>
      <section v-if="mode === 'contextual'">
        <h2 class="h5">Contexto de evoluções</h2>
        <p>Prepare a seleção em Faturamento de evoluções. O servidor recarrega os IDs e apura os grupos e valores.</p>
        <button v-if="canFiscal('nfse.preview')" class="btn btn-primary mb-3" :disabled="!state.operation || state.busy || state.sent" @click="preview">Revalidar e gerar preview</button>
      </section>
      <form v-if="canFiscal('nfse.documents.view')" class="my-3" @submit.prevent="recover()">
        <label for="nfse-operation">Consultar operação por identificação</label>
        <div class="input-group"><input id="nfse-operation" v-model="operationId" class="form-control" placeholder="Identificação da operação" required /><div class="input-group-append"><button class="btn btn-outline-primary" :disabled="state.busy" type="submit">Consultar operação</button></div></div>
      </form>
      <section v-if="summary" class="border rounded p-3 my-3">
        <h2 class="h5">Operação {{ summary.operation_id }}</h2>
        <p><span :class="'badge badge-' + (partialResult ? 'warning' : statusVariant(summary.status))">{{ partialResult ? 'Resultado parcial — consulte os grupos' : statusLabel(summary.status) }}</span> · Versão {{ summary.version }}<template v-if="summary.mode !== 'manual'"> · Total do servidor: <strong>{{ money(summary.total) }}</strong></template></p>
        <p>Tomador fiscal: <strong>{{ fiscalSnapshot.tomador || 'Não informado' }}</strong> · Competência: <strong>{{ fiscalSnapshot.competencia || 'Não informada' }}</strong></p>
        <p v-if="summary.expires_at">Validade do contexto: {{ summary.expires_at }}</p>
        <p v-if="previewExpired" class="alert alert-warning">Preview expirado. Revalide o contexto antes de confirmar.</p>
        <p v-if="needsReconciliation(summary)" class="alert alert-warning">Reconciliação pendente. Consulte esta operação; não retransmita os grupos.</p>
        <ul v-if="summary.warnings && summary.warnings.length" class="alert alert-info"><li v-for="(warning, index) in summary.warnings" :key="index">{{ warning }}</li></ul>
        <div class="table-responsive"><table class="table table-sm">
          <caption>Estados e valores por grupo, conforme resposta fiscal. Grupo autorizado não pode ser retransmitido.</caption>
          <thead><tr><th>Grupo</th><th>Estado</th><th>Código / origem</th><th>Bruto</th><th>Descontos</th><th>Base</th><th>Tributo devido</th><th>Retido</th><th>Líquido</th></tr></thead>
          <tbody><tr v-for="group in summary.groups" :key="group.group_id"><td>{{ group.group_id }}</td><td>{{ statusLabel(group.status) }}<span v-if="group.reconciliation_required_at"> · Reconciliação pendente</span></td><td>{{ group.service_code || 'Não informado' }} / {{ group.code_source || 'unknown' }}</td><td>{{ money(group.gross) }}</td><td>{{ money(group.discounts) }}</td><td>{{ money(group.tax_base) }}</td><td>{{ money(group.taxes && group.taxes.iss_due) }}</td><td>{{ money(group.taxes && group.taxes.iss_retained) }}</td><td>{{ money(group.net) }}</td></tr></tbody>
        </table></div>
        <p v-if="!summary.groups || !summary.groups.length" class="text-muted">Grupos fiscais não informados pela API.</p>
      </section>
      <div v-if="mode !== 'external' && canFiscal('nfse.emit')" class="border-top pt-3">
        <label class="d-block"><input v-model="confirmed" type="checkbox" :disabled="!state.preview || state.busy || state.sent || previewExpired" /> Conferi tomador, competência, serviços, grupos, tributos e valores deste preview e confirmo a emissão.</label>
        <button data-test="emit" class="btn btn-success" :disabled="!canEmit || previewExpired" @click="emit">{{ state.busy ? 'Aguarde…' : 'Confirmar emissão' }}</button>
      </div>
    </div></div>
    <section v-if="canFiscal('nfse.documents.view')" class="card"><div class="card-body">
      <h2 class="h4">Documentos NFS-e</h2>
      <form class="d-flex flex-wrap align-items-end mb-3" @submit.prevent="loadDocuments(1)"><label class="mr-3">Estado fiscal<select v-model="documentFilters.status" class="form-control"><option value="">Todos</option><option v-for="(label, status) in fiscalStates" :key="status" :value="status">{{ label }}</option></select></label><button type="submit" class="btn btn-outline-primary mb-2" :disabled="state.documentsBusy">Consultar documentos</button></form>
      <p class="text-muted">XML/PDF: endpoint não confirmado para acesso autorizado aos artefatos. Downloads indisponíveis até a confirmação do contrato.</p>
      <div class="table-responsive"><table class="table">
        <thead><tr><th>Documento</th><th>Número</th><th>Estado fiscal</th><th>Evoluções</th><th>Ações</th></tr></thead>
        <tbody>
          <tr v-for="document in state.documents" :key="document.id"><td>{{ document.id }}</td><td>{{ document.numero || document.numero_nfs || '—' }}</td><td>{{ statusLabel(document.status) }}<span v-if="document.reconciliation_required_at"> · Reconciliação pendente</span></td><td>{{ document.evolution_count == null ? '—' : document.evolution_count }}</td><td><button v-if="canFiscal('nfse.cancel') && document.status === 'authorized'" class="btn btn-sm btn-outline-danger" :disabled="state.busy || state.cancelPending[document.id]" @click="startCancellation(document)">Solicitar cancelamento</button><span v-else>—</span></td></tr>
          <tr v-if="!state.documents.length"><td colspan="5">{{ state.documentsBusy ? 'Consultando…' : 'Nenhum documento carregado.' }}</td></tr>
        </tbody>
      </table></div>
      <nav aria-label="Paginação dos documentos"><button class="btn btn-outline-secondary mr-2" :disabled="state.documentsBusy || documentFilters.page <= 1" @click="loadDocuments(documentFilters.page - 1)">Anterior</button><span>Página {{ documentFilters.page }} de {{ documentPages }}</span><button class="btn btn-outline-secondary ml-2" :disabled="state.documentsBusy || documentFilters.page >= documentPages" @click="loadDocuments(documentFilters.page + 1)">Próxima</button></nav>
      <form v-if="cancelTarget" class="alert alert-warning mt-3" @submit.prevent="cancel">
        <h3 class="h5">Cancelar documento {{ cancelTarget.numero || cancelTarget.id }}</h3>
        <p>O pedido abrange todas as evoluções vinculadas ao documento. Cancelamento fiscal não baixa, estorna ou exclui registros financeiros.</p>
        <label class="d-block">Motivo *<textarea v-model="cancelReason" class="form-control" required :disabled="state.busy"></textarea></label>
        <label class="d-block"><input v-model="cancelConfirmed" type="checkbox" :disabled="state.busy" /> Confirmo a solicitação de cancelamento integral deste documento.</label>
        <button class="btn btn-danger mr-2" :disabled="state.busy || !cancelConfirmed || !cancelReason.trim()" type="submit">Solicitar cancelamento</button><button class="btn btn-secondary" type="button" :disabled="state.busy" @click="cancelTarget = null">Voltar</button>
      </form>
    </div></section>
  </Layout>
</template>
